import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { delay } from "../helper/delayresponse";

export interface AgentMonitor {
  id?: number;
  name?: string;
  email?: string;
  phone_number?: string;
  status?: number;
  role?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface AgentsAndMonitorsState {
  agentmonitordata: AgentMonitor[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

export const GetallAgentsAndMonitors = createAsyncThunk<
  {
    data: AgentMonitor[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number },
  { rejectValue: string }
>(
  "GetallAgentsAndMonitors",
  async ({ search, page, limit }, { rejectWithValue }) => {
    try {
      await delay(1000)
      const response = await axiosInstance.get(`/api/admin/users`, {
        params: { search, page, limit },
      });
      return { data: response.data.data, info: response.data.info };
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

// ==================view-agent/monitor-api-start=============
export const ViewAgentMonitor = createAsyncThunk<
  AgentMonitor,
  number,
  { rejectValue: string }
>("agentmonitordata/ViewAgentMonitor", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`api/admin/users/${id}`);
    return response.data.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || "An error occurred";
    return rejectWithValue(errorMessage);
  }
});
// ==================view-agent/monitor-api-start=============

// ==================Addagentsmonitors-action-start===========
export const Addagentsmonitors = createAsyncThunk<
  AgentMonitor,
  AgentMonitor,
  { rejectValue: string }
>("agentmonitordata/Addagentsmonitors", async (args, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/api/admin/users/create`, args);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response.data);
    }
    return rejectWithValue(error.message);
  }
});
// ==================Addagentsmonitors-action-end===========

// =================Updateagentmonitors-action-start=========
export const Updateagentmonitors = createAsyncThunk<
  AgentMonitor,
  { id: number; updatedData: AgentMonitor },
  { rejectValue: string }
>(
  "agentmonitordata/Updateagentmonitors",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/admin/users/update/${id}`,
        updatedData
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// =================Updateagentmonitors-action-end=========

// ================Delete-agent/monitor-start=========
export const Deleteagentmonitors = createAsyncThunk<
  { id: number },
  { id: number },
  { rejectValue: string }
>(
  "agentmonitordata/Deleteagentmonitors",
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.delete(`/api/admin/users/${id}`);
      return data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return error.response.data;
      }
      return error.message;
    }
  }
);
// ================Delete-agent/monitor-end=========

const initialState: AgentsAndMonitorsState = {
  agentmonitordata: [],
  info: null,
  error: null,
  loading: false,
};

const agentmonitorSlice = createSlice({
  name: "Agentmonitor",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===============get-all-agentmonitor-slice-start============
      .addCase(GetallAgentsAndMonitors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetallAgentsAndMonitors.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: AgentMonitor[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.agentmonitordata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetallAgentsAndMonitors.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ===============get-all-agentmonitor-slice-end============

    // ===============View-agentmonitor-slice-start============
    builder
      .addCase(ViewAgentMonitor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        ViewAgentMonitor.fulfilled,
        (state, action: PayloadAction<AgentMonitor>) => {
          state.loading = false;
          state.error = null;
          state.agentmonitordata = [action.payload];
        }
      )
      .addCase(ViewAgentMonitor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // ===============View-agentmonitor-slice-end============

    // =================add-agentmonitor-slice-start============
    builder
      .addCase(Addagentsmonitors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        Addagentsmonitors.fulfilled,
        (state, action: PayloadAction<AgentMonitor>) => {
          state.loading = false;
          state.agentmonitordata.push(action.payload);
        }
      )
      .addCase(
        Addagentsmonitors.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to add agent/monitor";
        }
      );
    // =================add-agentmonitor-slice-end============

    // =================Update agent/monitor-slice-start============
    builder
      .addCase(Updateagentmonitors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        Updateagentmonitors.fulfilled,
        (state, action: PayloadAction<AgentMonitor>) => {
          state.loading = false;
          const index = state.agentmonitordata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.agentmonitordata[index] = action.payload;
          }
        }
      )
      .addCase(
        Updateagentmonitors.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update agent/monitor";
        }
      );
    // =================Update agent/monitor-slice-start============

    // =================Delete agent/monitor-slice-start============
    builder
      .addCase(Deleteagentmonitors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        Deleteagentmonitors.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          state.loading = false;
        }
      )
      .addCase(
        Deleteagentmonitors.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to delete agent/monitor";
        }
      );
    // =================Delete agent/monitor-slice-end============
  },
});

export default agentmonitorSlice.reducer;
